import { formatQuantity } from '../../utils/inventoryLabels';
import { matchCodes, sameSlotCode } from '../operator/operatorCodes';

/*
 * The rules of the guided picking walk: which line comes next, where to take
 * it from, what a scanned code is, and what to say once the server answered.
 */

export const PICKING_STEP = {
  SLOT: 'slot',
  PRODUCT: 'product',
  QUANTITY: 'quantity',
  DONE: 'done',
};

export const pendingOf = (line) => Number(line?.pending || 0);

/** «1 línea», «3 líneas». */
export const linesText = (count) => `${count} ${Number(count) === 1 ? 'línea' : 'líneas'}`;

/** The first place a line can be taken from: its suggestions come in route order. */
export const firstTarget = (line) =>
  (line?.suggestions || []).find((suggestion) => Number(suggestion.available) > 0) || null;

/**
 * The line the walk goes to: the first in route order that still lacks
 * something, can be taken from somewhere and was not skipped; once those are
 * done, the skipped ones come back. `null` when nothing left can be picked —
 * a line with nowhere to take it from is reported, not walked to.
 *
 * @param {Array<object>} lines - In route order.
 * @param {number[]} skipped
 * @returns {number|null}
 */
export const nextLineId = (lines, skipped) => {
  const pickable = (lines || []).filter((line) => pendingOf(line) > 0 && firstTarget(line));
  const fresh = pickable.find((line) => !skipped.includes(line.line_id));
  return (fresh || pickable[0])?.line_id ?? null;
};

/**
 * Whether tapping a line goes to it: another than the one on screen, that
 * still lacks something and can be taken from somewhere.
 */
export const canChooseLine = (line, walk) =>
  pendingOf(line) > 0 && Boolean(firstTarget(line)) && line.line_id !== walk?.lineId;

/** Lines that still lack something but have nowhere left to be taken from. */
export const unpickableLines = (lines) =>
  (lines || []).filter((line) => pendingOf(line) > 0 && !firstTarget(line));

/** A suggestion of the line whose slot the code is. */
export const matchSuggestion = (line, code) =>
  (line?.suggestions || []).find((suggestion) => sameSlotCode(suggestion.slot_code, code)) || null;

/** The other line a product code belongs to, to say so instead of "no". */
export const otherLineFor = (lines, currentLineId, code) =>
  (lines || []).find((line) => line.line_id !== currentLineId && matchCodes(line.codes, code)) || null;

/** Why a pick cannot go yet, written for the button; or `null`. */
export const pickQuantityProblem = (quantity, line, factor) => {
  const amount = Number(quantity);
  if (!(amount > 0)) return 'Indica cuántos sacas';
  if (!Number.isInteger(amount)) return 'Se pickea en unidades enteras';
  if (amount * factor > pendingOf(line)) return `Faltan ${formatQuantity(pendingOf(line))} un en esta línea`;
  return null;
};

/** Why a short pick cannot go yet, or `null`. */
export const shortPickProblem = ({ found, reason }) => {
  const amount = Number(found);
  if (found === '' || Number.isNaN(amount) || amount < 0) return 'Indica cuántos encontraste';
  if (!Number.isInteger(amount)) return 'Se cuenta en unidades enteras';
  if (!reason) return 'Elige el motivo';
  return null;
};

/** A lot sent, even "", is fixed by the server; one left out lets it take the only one. */
const withLot = (lot) => (lot != null ? { lot } : {});

/** @param {{productCode: string, slotCode: string, quantity: number, lineId: number, lot?: string}} arg */
export const buildPickBody = ({ productCode, slotCode, quantity, lineId, lot }, key) => ({
  product_code: productCode,
  slot_code: slotCode,
  quantity: Number(quantity),
  line_id: lineId,
  ...withLot(lot),
  idempotency_key: key,
});

/**
 * Finding less than the system says. Found units travel with the product's
 * code, which the server needs whenever something was found.
 */
export const buildShortPickBody = ({ lineId, slotCode, found, reason, lot, notes, productCode }, key) => ({
  line_id: lineId,
  slot_code: slotCode,
  found_quantity: Number(found),
  reason,
  ...(Number(found) > 0 && productCode ? { product_code: productCode } : {}),
  ...withLot(lot),
  ...(notes ? { notes } : {}),
  idempotency_key: key,
});

export const buildUnpickBody = ({ lineId, slotCode, quantity, lot, reason }, key) => ({
  line_id: lineId,
  slot_code: slotCode,
  quantity: Number(quantity),
  ...withLot(lot),
  ...(reason ? { reason } : {}),
  idempotency_key: key,
});

/** What to say once a pick went through. */
export const pickMessage = (payload, arg) => {
  if (payload.replayed) return { tone: 'success', message: 'Ese pick ya estaba registrado.' };
  const units = Number(arg.quantity) * (Number(arg.factor) || 1);
  const line = payload.line || {};
  if (Number(payload.order?.pending_units) === 0) {
    return { tone: 'success', message: `${formatQuantity(units)} un de ${line.sku}. Orden pickeada completa.` };
  }
  const rest = pendingOf(line) > 0 ? `Faltan ${formatQuantity(pendingOf(line))} un.` : 'Línea completa.';
  return { tone: 'success', message: `${formatQuantity(units)} un de ${line.sku} desde ${arg.slotName || arg.slotCode}. ${rest}` };
};

/**
 * What to say once a missing quantity was reported: and where to try next,
 * while the order is on screen to act on it.
 */
export const shortPickMessage = (payload, arg, { onScreen = true } = {}) => {
  if (payload.replayed) return { tone: 'success', message: 'Ese desvío ya estaba registrado.' };
  const where = arg.slotName || arg.slotCode;
  const next = onScreen && (payload.line?.suggestions || []).find((item) => Number(item.available) > 0);
  return {
    tone: 'warning',
    message: `Desvío registrado en ${where}${next ? `. Prueba en ${next.slot_name || next.slot_code}` : ''}.`,
  };
};

/**
 * Whether the order can be reserved from the floor: its client's contract does
 * not reserve on entry (`auto_reserve` off), and part of it is not reserved
 * yet. Reserving is optional: picking goes on without it.
 */
export const needsReserve = (order) => order?.auto_reserve === false && Number(order.unreserved_units) > 0;

/** The queue's chip: nothing reserved yet, or only part. */
export const reserveTagText = (order) => (Number(order?.reserved_units) > 0 ? 'Reserva incompleta' : 'Sin reservar');

/** What the walk says is not reserved. */
export const reserveNoteText = (order) =>
  Number(order?.reserved_units) > 0
    ? `Faltan ${formatQuantity(order.unreserved_units)} un por reservar`
    : `Sin reservar · ${formatQuantity(order?.unreserved_units)} un`;

/**
 * What to say once the order was reserved: how much now, and whether part of
 * it had no free stock — picking goes on all the same. It names the order, so
 * off screen it is said as is, without what to do next.
 */
export const reserveMessage = (payload, arg, { onScreen = true } = {}) => {
  const order = payload.order || {};
  const name = order.reference_id || `Orden #${arg.orderId}`;
  const now = Number(payload.reserved_now) || 0;
  const missing = Number(order.unreserved_units) || 0;
  const goOn = (text) => (onScreen ? ` ${text}` : '');
  if (missing === 0) {
    return {
      tone: 'success',
      message: now > 0 ? `Reservaste ${formatQuantity(now)} un: ${name} quedó reservada.` : `${name} ya estaba reservada.`,
    };
  }
  if (now > 0) {
    return {
      tone: 'warning',
      message: `Reservaste ${formatQuantity(now)} un de ${name}; faltan ${formatQuantity(missing)} un sin stock libre.${goOn(
        'Puedes pickear igual.'
      )}`,
    };
  }
  return {
    tone: 'warning',
    message: `No hay stock libre para reservar ${name}.${goOn('Puedes pickear igual lo que encuentres.')}`,
  };
};

export const unpickMessage = (payload, arg) =>
  payload.replayed
    ? { tone: 'success', message: 'Esa devolución ya estaba registrada.' }
    : { tone: 'success', message: `${formatQuantity(arg.quantity)} un devueltas a ${arg.slotCode}.` };
